/**
 * Report facts — layer 2 of 4.
 *
 * Every calculation, threshold, ratio and boolean the report needs, resolved
 * once. NO SENTENCES live here.
 *
 * The constraint worth enforcing in review: the rules layer may not contain an
 * arithmetic operator. If a sentence needs a number this file does not already
 * expose, add it here rather than computing it inline — that is what lets a
 * compliance reviewer read the copy file, and what stops the same threshold
 * being written two different ways in two different sentences.
 *
 * Sanity gates (spec §11.1) also live here. A value that fails its gate is
 * suppressed along with every sentence that cites it, and recorded in
 * `suppressed` so it can be raised server-side — a silently hidden paragraph
 * is a bug nobody finds.
 */

import { buildPlanAnalytics, liabilityMonthlySip } from "../analytics";
import { fvSIP, monthlyEquivalent, sipForTarget } from "../tvm";
import { corpusForYears } from "../retirement";
import type {
  PlanResult,
  PlannerFund,
  PlannerGoalResult,
  PlannerLiabilityResult,
  PlannerSwpResult,
} from "../types";

/* ───────────────────────────── thresholds ───────────────────────────── */

/** Every number the copy branches on, in one place. */
export const THRESHOLDS = {
  /** Below this a goal barely compounds and the copy says so. */
  nearGoalMonths: 12,
  /** Inflation uplift beyond which the default assumption is worth naming. */
  bluntUpliftPct: 10,
  /** Idle cash above this share of the lumpsum escalates to a caution. */
  idleLargePctOfLumpsum: 25,
  /** Interest above this share of principal earns the "almost X%" clause. */
  interestLargeSharePct: 50,
  /** A return assumption only worth caveating over a decade or more. */
  longHorizonMonths: 120,
  /** A car loan beyond either of these is probably misclassified. */
  carLoanOddMonths: 84,
  carLoanOddAmount: 2_500_000,
  /** Quoted and computed yields differing by more than this get a footnote. */
  ytmDivergence: 0.05,
  /** Spare below this share of surplus is "a little to spare". */
  smallSparePctOfSurplus: 15,
  /** Retirement severity bands, in years the corpus lasts. */
  corpusCriticalYears: 10,
  corpusCautionYears: 20,
  /** The horizon a sustainable retirement income is sized against. */
  retirementHorizonYears: 25,
  /** Below this the drawdown is long enough that the age itself is a finding. */
  earlyRetirementAge: 55,
  /** At or above this slab, debt taxation is worth naming as a cost. */
  highTaxBracketPct: 20,
  /**
   * Boxed findings per section per subject before the rest is demoted.
   *
   * Two. A section page also carries its lede, its figures, a table and a
   * footnote, and a third panel is what tips it onto a second page — measured,
   * not guessed: at three, the family-trip and car-loan sections each ran to
   * two pages. Nothing is lost; the third moves to the overflow appendix, which
   * is what that appendix is for.
   */
  calloutBudget: 2,
} as const;

/* ─────────────────────────────── gates ──────────────────────────────── */

export interface GateFailure {
  /** Stable id, so a failure can be alerted on and traced. */
  id: string;
  subject: string;
  detail: string;
}

class Gates {
  readonly failures: GateFailure[] = [];

  check(ok: boolean, id: string, subject: string, detail: string): boolean {
    if (!ok) this.failures.push({ id, subject, detail });
    return ok;
  }
}

/* ─────────────────────────────── facts ──────────────────────────────── */

export interface FundFacts {
  count: number;
  distinctAmcCount: number;
  anyScored: boolean;
  allUnscored: boolean;
  topAmc: string | null;
  topAmcWeightPct: number;
  capSatisfied: boolean;
  /** How far over the cap the worst house sits, in points. */
  capExcessPct: number;
  /**
   * The concentration copy differs for each: everything in one fund, several
   * funds all from one house, or one house simply over the ceiling. Two houses
   * at 50% each breaches the cap but is NOT "the same house" — conflating them
   * puts a sentence in the report that contradicts the table beside it.
   */
  concentration: "none" | "single-fund" | "single-house" | "over-cap";
  /** Payout plans compound nothing; the tone escalates with horizon. */
  dividendPlans: string[];
  /**
   * Share of the basket taxed as debt — added to income at slab, whatever the
   * holding period. At a high bracket this is the most expensive treatment
   * available, and it is invisible in a table that shows only category names.
   */
  debtTaxedWeightPct: number;
  /** The schemes carrying it, so the copy can name them. */
  debtTaxedNames: string[];
}

export interface BondFacts {
  name: string;
  rating: string | null;
  couponPct: number;
  frequency: string;
  ytm: number | null;
  ytmValid: boolean;
  ytmComputed: number | null;
  ytmDiverges: boolean;
  pricedAtPar: boolean;

  units: number;
  lots: number | null;
  invested: number;
  totalFaceValue: number;
  atDiscount: boolean;
  atPremium: boolean;

  accruedInterest: number;
  accruedPresent: boolean;
  accruedNegative: boolean;
  inShutPeriod: boolean;
  settlementAmount: number;
  settlementDate: string;

  idle: number;
  idlePresent: boolean;
  idlePctOfLumpsum: number;
  idleIsLotArtifact: boolean;
  idleIsLarge: boolean;

  maturity: string;
  /**
   * TRUE when the date the plan counts on is a CALL, not a final maturity.
   *
   * A call is an option to redeem early. If it belongs to the issuer and they
   * decline to exercise it, the bond runs on and the principal does not arrive
   * when the goal needs it. The response says which via `bond.dateType`, and
   * the difference is material enough to stop an order over.
   */
  isCallDate: boolean;
  maturesBeforeGoal: boolean;
  gapDays: number;
  gapValid: boolean;

  couponCount: number;
  avgCouponAfterTds: number;
  parkingFundName: string | null;
}

export interface GoalFacts {
  id: number;
  /** As written, for headings. */
  label: string;
  /** For use inside a sentence — "the Family Trip". Never derived by casing. */
  phrase: string;
  type: string;
  segment: string;
  segTerm: string;
  years: number;
  months: number;
  totalMonths: number;
  tenureLabel: string;
  goalDate: string;

  returnPct: number;
  inflationPct: number;
  inflationIsDefault: boolean;
  upliftPct: number;
  inflationBlunt: boolean;
  inflationMayBeLow: boolean;
  isVeryNear: boolean;

  costToday: number;
  futureCost: number;
  coveredByLumpsum: number;
  coveragePct: number;
  coverageValid: boolean;
  /** The lumpsum route alone meets or exceeds the target. */
  fullyCovered: boolean;
  gap: number;
  sip: number;
  sipValid: boolean;
  sipFrequency: string;
  monthlySip: number;
  instalments: number;

  contributed: number;
  growth: number;
  returnSharePct: number;
  capitalMultiple: number;
  inflationMultiple: number;

  isLongest: boolean;
  usedLumpsum: boolean;
  hasLumpsum: boolean;
  mode: string;
  /** Whole years the client will be when this goal falls due. */
  ageAtGoal: number;
  /** Share of the target the lumpsum route delivers. */
  lumpsumSharePct: number;

  /** Set when the goal's segment and the client's appetite disagree. */
  riskMismatch: "debt-for-aggressive" | "equity-for-conservative" | null;

  bond: BondFacts | null;
  bondState:
    | "opted-out"
    | "none-eligible"
    | "one-eligible"
    | "several-eligible"
    | "not-this-goal";
  eligibleBondCount: number;
  bondUnavailableDetail: string | null;
  nearMissCount: number;

  funds: FundFacts;
  /**
   * Why this fund category rather than the obvious alternative. Debt interest
   * is taxed at slab, so the answer differs sharply by bracket.
   */
  categoryFit: "debt-at-nil" | "arbitrage-at-high" | null;
  taxNote: string;
  sourcing: string;
}

export interface LiabilityFacts {
  label: string;
  phrase: string;
  type: string;
  outstanding: number;
  ratePct: number;
  emi: number;
  totalMonths: number;
  tenureLabel: string;
  years: number;
  months: number;

  totalInterest: number;
  interestSharePct: number;
  interestIsLarge: boolean;

  segment: string;
  assumedReturnPct: number;
  sip10: number;
  sip10FV: number;
  cancels: boolean;
  sipNeeded: number;
  monthlySip: number;
  topUp: number;
  /** Months the loan had already run; 0 for a new loan. */
  elapsedMonths: number;
  elapsedInterest: number;
  /** Extra SIP making up for the months already passed; 0 for a new loan. */
  catchUpSip: number;

  isLongHorizon: boolean;
  classificationLooksOdd: boolean;
  prepayBeatsInvesting: boolean;
  runsIntoRetirement: boolean;
  overlapMonths: number;

  funds: FundFacts;
  sourcing: string;
}

export interface SwpFacts {
  startDate: string;
  returnPct: number;
  inflationPct: number;
  frequency: string;
  withdrawal: number;
  pension: number;
  net: number;
  startCorpus: number;
  retirementCorpus: number;
  nothingIsBuildingIt: boolean;
  pensionSurplus: number;
  pensionCoversIt: boolean;

  exhausted: boolean;
  everlasting: boolean;
  lastsYears: number;
  lastsWholeYears: number;
  lastsMonths: number;
  severity: "critical" | "caution" | "info";

  withdrawalRatePct: number;
  rateValid: boolean;
  /** What sustaining the income for the planning horizon would need. */
  sustainableCorpus: number;
  corpusShortfall: number;
  /**
   * `sustainableCorpus` discounted back to today at the retirement goal's own
   * inflation — "roughly ₹3.51 crore in today's money", which is the number a
   * client can compare against the target they actually chose.
   */
  sustainableInTodaysMoney: number;
  /** `sustainableCorpus` ÷ what the plan builds. "seven times". */
  sustainableMultiple: number;
  /** What the uncommitted surplus grows to by the drawdown date, if invested. */
  spareProjected: number;
  /** Whether investing it actually closes the shortfall. */
  spareClosesGap: boolean;
}

export interface TaxFacts {
  bracketPct: number;
  tdsPct: number;
  state: "refundable" | "neutral" | "short";
  /** TDS withheld across every bond coupon in the plan. */
  tdsWithheld: number;
  /** Difference between withheld and owed — a refund, or a balance payable. */
  balance: number;
  /** A nil bracket sitting implausibly beside real money is a data-quality prompt. */
  confirmBracket: boolean;
}

export interface LadderStep {
  fromMonth: number;
  toMonth: number | null;
  spare: number;
  endsNext: string | null;
}

export interface PlanFacts {
  clientName: string;
  isHuf: boolean;
  riskProfile: string;
  investorType: string;
  firstTimer: boolean;
  shariah: boolean;
  bondsOn: boolean;

  /** Age today, in months — goal dates mean little without it. */
  clientAgeMonths: number;

  goalCount: number;
  liabilityCount: number;
  hasSwp: boolean;
  /** Goals + loans + drawdown — what "this plan covers N things" counts. */
  partCount: number;
  /** Rows in `plan.orders`. Zero means the orders section is not built. */
  orderCount: number;
  liabilityTypesUniform: boolean;
  liabilityNoun: string;

  surplus: number;
  surplusRecorded: boolean;
  committed: number;
  spare: number;
  shortfall: number;
  committedSharePct: number;
  spareSharePct: number;
  /** False when the plan is met entirely by the lumpsum — no SIP anywhere. */
  requiresMonthly: boolean;
  affordability: "unrecorded" | "short" | "tight" | "comfortable";

  totalFutureCost: number;
  totalContributed: number;
  totalGrowth: number;
  lumpsum: number;

  ladder: LadderStep[];
  goals: GoalFacts[];
  liabilities: LiabilityFacts[];
  swp: SwpFacts | null;
  tax: TaxFacts;

  /** Sanity-gate failures. Raise these server-side, don't just hide them. */
  suppressed: GateFailure[];
}

/* ──────────────────────────── fund analysis ─────────────────────────── */

const deriveFunds = (funds: PlannerFund[], capSatisfied: boolean): FundFacts => {
  const byAmc = new Map<string, number>();
  const total = funds.reduce((sum, fund) => sum + fund.amount, 0);
  funds.forEach((fund) => {
    byAmc.set(fund.amc, (byAmc.get(fund.amc) ?? 0) + fund.amount);
  });

  let topAmc: string | null = null;
  let topAmount = 0;
  byAmc.forEach((amount, amc) => {
    if (amount > topAmount) {
      topAmount = amount;
      topAmc = amc;
    }
  });

  const topAmcWeightPct = total > 0 ? (topAmount / total) * 100 : 0;
  const distinctAmcCount = byAmc.size;

  return {
    count: funds.length,
    distinctAmcCount,
    anyScored: funds.some((fund) => fund.score != null),
    allUnscored: funds.length > 0 && funds.every((fund) => fund.score == null),
    topAmc,
    topAmcWeightPct,
    capSatisfied,
    capExcessPct: Math.max(0, topAmcWeightPct - 20),
    concentration:
      capSatisfied || funds.length === 0
        ? "none"
        : funds.length === 1
          ? "single-fund"
          : distinctAmcCount === 1
            ? "single-house"
            : "over-cap",
    dividendPlans: funds
      .filter((fund) => /idcw|dividend|payout/i.test(fund.name))
      .map((fund) => fund.name),
    debtTaxedWeightPct: funds
      .filter((fund) => fund.tax === "debt")
      .reduce((sum, fund) => sum + fund.weightPct, 0),
    debtTaxedNames: funds
      .filter((fund) => fund.tax === "debt")
      .map((fund) => fund.name),
  };
};

/* ──────────────────────────── bond analysis ─────────────────────────── */

const deriveBond = (
  goal: PlannerGoalResult,
  lumpsum: number,
  gates: Gates
): BondFacts | null => {
  const bond = goal.bond;
  if (!bond) return null;

  const subject = `goal ${goal.id}`;
  // A negative or absurd yield next to a recommendation destroys trust in
  // every number the reader cannot check as well as the one they can.
  const ytmValid = gates.check(
    bond.ytm > 0 && bond.ytm <= 30,
    "bond.ytm",
    subject,
    `yield to maturity out of range: ${bond.ytm}`
  );
  const gapValid = gates.check(
    bond.maturesBeforeGoal || bond.gapDays > 0,
    "bond.gapDays",
    subject,
    `bond matures after the goal but gapDays is ${bond.gapDays}`
  );

  const oneMoreLot = (bond.unitPrice ?? 0) * (bond.lotUnits || 1);
  const idlePctOfLumpsum = lumpsum > 0 ? (bond.idle / lumpsum) * 100 : 0;

  return {
    name: bond.name,
    rating: bond.rating || null,
    couponPct: bond.coupon,
    frequency: bond.freq,
    ytm: ytmValid ? bond.ytm : null,
    ytmValid,
    ytmComputed: bond.ytmComputed ?? null,
    ytmDiverges:
      ytmValid &&
      bond.ytmComputed != null &&
      Math.abs(bond.ytm - bond.ytmComputed) > THRESHOLDS.ytmDivergence,
    pricedAtPar: bond.pricedAtPar,

    units: bond.units,
    lots: bond.lots ?? null,
    invested: bond.invested,
    totalFaceValue: bond.totalFaceValue,
    atDiscount: ytmValid && bond.invested < bond.totalFaceValue,
    atPremium: bond.invested > bond.totalFaceValue,

    accruedInterest: bond.accruedInterest,
    accruedPresent: bond.accruedInterest !== 0,
    accruedNegative: bond.accruedInterest < 0,
    inShutPeriod: bond.inShutPeriod,
    settlementAmount: bond.settlementAmount,
    settlementDate: bond.settlementDate,

    idle: bond.idle,
    idlePresent: bond.idle > 0,
    idlePctOfLumpsum,
    idleIsLotArtifact: bond.idle > 0 && bond.idle < oneMoreLot,
    idleIsLarge: idlePctOfLumpsum > THRESHOLDS.idleLargePctOfLumpsum,

    maturity: bond.maturity,
    isCallDate: bond.dateType === "callDate",
    maturesBeforeGoal: bond.maturesBeforeGoal,
    gapDays: bond.gapDays,
    gapValid,

    couponCount: bond.cashflows.filter((row) => row.type === "Interest").length,
    avgCouponAfterTds: bond.avgCouponAfterTds,
    parkingFundName: bond.parkingFund?.name ?? null,
  };
};

/* ──────────────────────────── goal analysis ─────────────────────────── */

const deriveGoal = (
  goal: PlannerGoalResult,
  plan: PlanResult,
  stats: ReturnType<typeof buildPlanAnalytics>["goals"][number],
  inflationDefault: number,
  gates: Gates
): GoalFacts => {
  const subject = `goal ${goal.id}`;
  const sipValid = gates.check(
    Number.isFinite(goal.sip) && goal.sip >= 0,
    "goal.sip",
    subject,
    `instalment is not a usable number: ${goal.sip}`
  );
  const coveragePct =
    goal.futureCost > 0 ? (goal.coveredByLumpsum / goal.futureCost) * 100 : 0;
  /* Only a negative or non-finite coverage is malformed. Exceeding 100% is a
     real and legitimate state — a small goal behind a large lumpsum — and
     suppressing it hid the most useful thing about such a plan. */
  const coverageValid = gates.check(
    coveragePct >= 0 && Number.isFinite(coveragePct),
    "goal.coverage",
    subject,
    `coverage is negative or not a number: ${coveragePct}`
  );

  const totalMonths = goal.totalMonths ?? Math.round(goal.years * 12);
  const inflationIsDefault = goal.infl === inflationDefault;
  const upliftPct =
    goal.costToday > 0 ? (goal.futureCost / goal.costToday - 1) * 100 : 0;
  const discretionary = /travel|vacation|marriage|car|wedding/i.test(goal.type);
  const escalating = /education|medical|health/i.test(goal.type);

  const riskMismatch =
    goal.seg === "Debt" && plan.client.risk === "Aggressive"
      ? ("debt-for-aggressive" as const)
      : goal.seg === "Equity" && plan.client.risk === "Conservative"
        ? ("equity-for-conservative" as const)
        : null;

  const eligibleBondCount = goal.eligibleBonds?.length ?? 0;
  const bondState = !plan.client.investBonds
    ? ("opted-out" as const)
    : !goal.usedLumpsum
      ? ("not-this-goal" as const)
      : goal.bond
        ? eligibleBondCount <= 1
          ? ("one-eligible" as const)
          : ("several-eligible" as const)
        : ("none-eligible" as const);

  return {
    id: goal.id,
    label: goal.name,
    // A heading form and a sentence form are two different strings — never
    // derive one from the other. "the Retirement" reads as an error; a name
    // that already carries its own article must not gain a second one.
    phrase: /^(the|your|my|a|an)\s/i.test(goal.name)
      ? goal.name
      : /retire/i.test(goal.type)
        ? "your retirement"
        : `the ${goal.name}`,
    type: goal.type,
    segment: goal.seg,
    segTerm: goal.segTerm,
    years: goal.years,
    months: goal.months,
    totalMonths,
    tenureLabel: goal.tenureLabel ?? `${goal.years}y`,
    goalDate: goal.goalDate,

    returnPct: goal.ret,
    inflationPct: goal.infl,
    inflationIsDefault,
    upliftPct,
    inflationBlunt:
      inflationIsDefault && discretionary && upliftPct > THRESHOLDS.bluntUpliftPct,
    inflationMayBeLow: inflationIsDefault && escalating,
    isVeryNear: totalMonths < THRESHOLDS.nearGoalMonths,

    costToday: goal.costToday,
    futureCost: goal.futureCost,
    coveredByLumpsum: goal.coveredByLumpsum,
    coveragePct: coverageValid ? coveragePct : 0,
    coverageValid,
    fullyCovered: coverageValid && coveragePct >= 100,
    gap: goal.gap,
    sip: goal.sip,
    sipValid,
    sipFrequency: goal.sipFreq,
    monthlySip: stats.monthlySip,
    instalments: stats.instalmentCount,

    contributed: stats.contributed,
    growth: stats.growth,
    returnSharePct: stats.returnSharePct,
    capitalMultiple: stats.capitalMultiple,
    inflationMultiple: stats.inflationMultiple,

    isLongest: goal.isLongest,
    ageAtGoal: Math.floor(
      (plan.client.ageY * 12 + plan.client.ageM + goal.totalMonths) / 12
    ),
    lumpsumSharePct:
      goal.futureCost > 0 ? (goal.coveredByLumpsum / goal.futureCost) * 100 : 0,
    usedLumpsum: goal.usedLumpsum,
    hasLumpsum: goal.coveredByLumpsum > 0,
    mode: goal.mode,
    riskMismatch,

    bond: deriveBond(goal, plan.summary.lumpsum, gates),
    bondState,
    eligibleBondCount,
    bondUnavailableDetail: goal.bondUnavailableDetail ?? null,
    nearMissCount: goal.nearMissBonds?.length ?? 0,

    funds: deriveFunds(goal.funds, goal.amcCapSatisfied),
    categoryFit:
      goal.seg !== "Debt"
        ? null
        : plan.client.taxBracket === 0
          ? "debt-at-nil"
          : plan.client.taxBracket >= 20
            ? "arbitrage-at-high"
            : null,
    taxNote: goal.taxNote,
    sourcing: goal.sourcing,
  };
};

/* ─────────────────────────── liability analysis ─────────────────────── */

const deriveLiability = (
  liability: PlannerLiabilityResult,
  swp: PlannerSwpResult | null
): LiabilityFacts => {
  const totalMonths = liability.totalMonths ?? liability.months;
  const monthlySip = liabilityMonthlySip(liability);
  const interestSharePct =
    liability.outstanding > 0
      ? (liability.totalInterest / liability.outstanding) * 100
      : 0;

  // Does the loan still have payments running once the drawdown starts?
  let overlapMonths = 0;
  if (swp?.startDate) {
    const start = new Date(`${swp.startDate}T00:00:00`);
    const now = new Date();
    const monthsToSwp =
      (start.getFullYear() - now.getFullYear()) * 12 +
      (start.getMonth() - now.getMonth());
    overlapMonths = Math.max(0, totalMonths - monthsToSwp);
  }

  return {
    label: liability.name,
    phrase: /^(the|your|my|a|an)\s/i.test(liability.name)
      ? liability.name
      : `the ${liability.name}`,
    type: liability.type,
    outstanding: liability.outstanding,
    ratePct: liability.rate,
    emi: liability.emi,
    totalMonths,
    tenureLabel: liability.tenureLabel ?? `${liability.years}y`,
    years: liability.years,
    months: liability.months,

    totalInterest: liability.totalInterest,
    interestSharePct,
    interestIsLarge: interestSharePct > THRESHOLDS.interestLargeSharePct,

    segment: liability.seg,
    assumedReturnPct: liability.ret,
    sip10: liability.sip10,
    sip10FV: liability.sip10FV,
    cancels: liability.cancels,
    sipNeeded: liability.sipNeeded,
    monthlySip,
    topUp: Math.max(0, liability.sipNeeded - liability.sip10),
    elapsedMonths: liability.elapsedMonths ?? 0,
    elapsedInterest: liability.elapsedInterest ?? 0,
    catchUpSip: liability.catchUp?.topUpSip ?? 0,

    isLongHorizon: totalMonths >= THRESHOLDS.longHorizonMonths,
    classificationLooksOdd:
      /car/i.test(liability.type) &&
      (totalMonths > THRESHOLDS.carLoanOddMonths ||
        liability.outstanding > THRESHOLDS.carLoanOddAmount),
    prepayBeatsInvesting: liability.rate > liability.ret,
    runsIntoRetirement: overlapMonths > 0,
    overlapMonths,

    funds: deriveFunds(liability.funds, liability.amcCapSatisfied),
    sourcing: liability.sourcing,
  };
};

/* ──────────────────────────── swp analysis ──────────────────────────── */

const deriveSwp = (
  swp: PlannerSwpResult,
  gates: Gates,
  spareProjected: number,
  /* The retirement goal's own timeline and inflation. Discounting a retirement
     target by the drawdown's inflation would mix two assumptions to answer one
     question, so the goal's is passed in rather than assumed. */
  horizon: { years: number; inflationPct: number }
): SwpFacts => {
  const annualWithdrawal = swp.net * 12;
  const withdrawalRatePct =
    swp.startCorpus > 0 ? (annualWithdrawal / swp.startCorpus) * 100 : 0;
  const rateValid = gates.check(
    withdrawalRatePct < 100,
    "swp.withdrawalRate",
    "retirement",
    `withdrawal rate of ${withdrawalRatePct.toFixed(1)}% — the plan is malformed`
  );

  /*
   * What the same income would need to last the planning horizon.
   *
   * This MUST be the same function the "What It Would Take" section prints its
   * table from. It used to be a separate real-rate annuity here, which put
   * "would need about ₹5.33 cr" in the summary against ₹4.93 cr in the table
   * two pages later — the exact failure mode of computing one figure twice.
   */
  const sustainableCorpus = corpusForYears(
    THRESHOLDS.retirementHorizonYears,
    swp.net,
    swp.ret,
    swp.infl
  );

  const lastsWholeYears = Math.floor(swp.lastsYears);

  return {
    startDate: swp.startDate,
    returnPct: swp.ret,
    inflationPct: swp.infl,
    frequency: swp.freq,
    withdrawal: swp.withdrawal,
    pension: swp.pension,
    net: swp.net,
    startCorpus: swp.startCorpus,
    retirementCorpus: swp.retirementCorpus,
    nothingIsBuildingIt: swp.retirementCorpus === 0,
    pensionSurplus: swp.pensionSurplusSIP,
    pensionCoversIt: swp.pensionSurplusSIP > 0,

    exhausted: swp.exhausted,
    everlasting: swp.everlasting,
    lastsYears: swp.lastsYears,
    lastsWholeYears,
    lastsMonths: Math.round((swp.lastsYears - lastsWholeYears) * 12),
    severity: !swp.exhausted
      ? "info"
      : swp.lastsYears < THRESHOLDS.corpusCriticalYears
        ? "critical"
        : swp.lastsYears < THRESHOLDS.corpusCautionYears
          ? "caution"
          : "info",

    withdrawalRatePct: rateValid ? withdrawalRatePct : 0,
    rateValid,
    sustainableCorpus,
    sustainableInTodaysMoney:
      sustainableCorpus / Math.pow(1 + horizon.inflationPct / 100, horizon.years),
    sustainableMultiple:
      swp.startCorpus > 0 ? sustainableCorpus / swp.startCorpus : 0,
    corpusShortfall: Math.max(0, sustainableCorpus - swp.startCorpus),
    spareProjected,
    spareClosesGap:
      spareProjected >= Math.max(0, sustainableCorpus - swp.startCorpus),
  };
};

/** Re-exported so the rules layer never has to reach for the maths itself. */
export const projectSpareInvested = (
  ladder: LadderStep[],
  returnPct: number,
  months: number
): number =>
  ladder.reduce((total, step) => {
    const to = step.toMonth ?? months;
    const span = Math.max(0, Math.min(to, months) - step.fromMonth + 1);
    if (span <= 0 || step.spare <= 0) return total;
    return total + fvSIP(step.spare, returnPct, span, "Monthly");
  }, 0);

/* ──────────────────────────── the whole plan ────────────────────────── */

export const derivePlanFacts = (
  plan: PlanResult,
  options: { inflationDefault?: number; tdsPct?: number } = {}
): PlanFacts => {
  const gates = new Gates();
  const analytics = buildPlanAnalytics(plan);
  const inflationDefault = options.inflationDefault ?? 8;
  const tdsPct = options.tdsPct ?? 10;

  const goals = plan.goals.map((goal, index) =>
    deriveGoal(goal, plan, analytics.goals[index], inflationDefault, gates)
  );
  const liabilities = plan.liabilities.map((liability) =>
    deriveLiability(liability, plan.swp)
  );
  const surplus = plan.summary.monthlySurplus;
  const committed = plan.summary.totalMonthlySIP;
  const spare = Math.max(0, surplus - committed);
  const shortfall = Math.max(0, committed - surplus);
  const committedSharePct = surplus > 0 ? (committed / surplus) * 100 : 0;
  const spareSharePct = surplus > 0 ? (spare / surplus) * 100 : 0;

  /* Anything that talks about money freeing up over time has to be built from
     every commitment, not from one. Sorted by end month, walked forward. */
  const ends = [
    ...goals.map((goal) => ({
      at: goal.totalMonths,
      amount: goal.monthlySip,
      what: `${goal.label} SIP completes`,
    })),
    ...liabilities.map((liability) => ({
      at: liability.totalMonths,
      amount: liability.monthlySip,
      what: `${liability.label} offset completes`,
    })),
  ].sort((a, b) => a.at - b.at);

  const ladder: LadderStep[] = [];
  let running = surplus - committed;
  let cursor = 1;
  ends.forEach((end, index) => {
    ladder.push({
      fromMonth: cursor,
      toMonth: end.at,
      spare: running,
      endsNext: end.what,
    });
    running += end.amount;
    cursor = end.at + 1;
    if (index === ends.length - 1) {
      ladder.push({ fromMonth: cursor, toMonth: null, spare: running, endsNext: null });
    }
  });

  /* What the uncommitted surplus becomes by the drawdown date, if invested at
     the SWP's own return. Needs the ladder, so it is derived after it. */
  const monthsToDrawdown = plan.swp?.startDate
    ? Math.max(
        0,
        Math.round(
          (new Date(`${plan.swp.startDate}T00:00:00`).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24 * 30.4375)
        )
      )
    : 0;
  /* The goal that actually builds the retirement corpus — its timeline is what
     "in today's money" has to be discounted over. */
  const retirementGoal =
    goals.find((goal) => goal.type === "Retirement") ??
    goals.find((goal) => goal.isLongest) ??
    goals[0];
  const swp = plan.swp
    ? deriveSwp(
        plan.swp,
        gates,
        projectSpareInvested(ladder, plan.swp.ret, monthsToDrawdown),
        {
          years: retirementGoal
            ? retirementGoal.totalMonths / 12
            : monthsToDrawdown / 12,
          inflationPct: retirementGoal?.inflationPct ?? inflationDefault,
        }
      )
    : null;

  const liabilityTypes = new Set(liabilities.map((liability) => liability.type));
  const tdsWithheld = goals.reduce((sum, goal) => {
    const bond = plan.goals.find((g) => g.id === goal.id)?.bond;
    if (!bond) return sum;
    return (
      sum +
      bond.cashflows
        .filter((row) => row.type === "Interest")
        .reduce((inner, row) => inner + (row.amount - row.afterTds), 0)
    );
  }, 0);
  const bracketPct = plan.client.taxBracket;

  return {
    clientName: plan.client.name,
    isHuf: /\bHUF\b/i.test(plan.client.name),
    riskProfile: plan.client.risk,
    investorType: plan.client.type,
    firstTimer: plan.client.firstTimer,
    shariah: plan.summary.shariah,
    bondsOn: plan.summary.bondsOn,

    goalCount: goals.length,
    liabilityCount: liabilities.length,
    hasSwp: swp != null,
    clientAgeMonths: plan.client.ageY * 12 + plan.client.ageM,
    partCount: goals.length + liabilities.length + (swp ? 1 : 0),
    orderCount: plan.orders?.length ?? 0,
    liabilityTypesUniform: liabilityTypes.size === 1,
    liabilityNoun:
      liabilityTypes.size === 1
        ? Array.from(liabilityTypes)[0].toLowerCase()
        : "loan",

    surplus,
    // Zero almost always means the field was skipped, not that the client has
    // nothing spare — the copy must say "recorded", never assert a shortfall.
    surplusRecorded: surplus > 0,
    committed,
    spare,
    shortfall,
    committedSharePct,
    spareSharePct,
    requiresMonthly: committed > 0,
    affordability:
      surplus <= 0
        ? "unrecorded"
        : committed > surplus
          ? "short"
          : spareSharePct < THRESHOLDS.smallSparePctOfSurplus
            ? "tight"
            : "comfortable",

    totalFutureCost: plan.summary.totalFutureCost,
    totalContributed: analytics.contributed,
    totalGrowth: analytics.wealthCreated,
    lumpsum: plan.summary.lumpsum,

    ladder,
    goals,
    liabilities,
    swp,
    tax: {
      bracketPct,
      tdsPct,
      state:
        bracketPct < tdsPct
          ? "refundable"
          : bracketPct === tdsPct
            ? "neutral"
            : "short",
      tdsWithheld,
      balance: Math.abs(tdsWithheld * (bracketPct / tdsPct - 1)),
      confirmBracket:
        bracketPct === 0 &&
        (surplus > 100_000 || plan.summary.totalFutureCost > 10_000_000),
    },

    suppressed: gates.failures,
  };
};

export { monthlyEquivalent, sipForTarget };
